import { absoluteUrl } from '@/lib/site-url';

const TWITTER_HANDLE = '@Ashkelon_News';

export type SharePayload = {
  /** Absolute article URL (without tracking params). */
  url: string;
  /** Prefill text - prefer subtitle, fall back to title. */
  text: string;
};

/** Build the public article URL used for sharing and SEO. */
export function articleShareUrl(article: {
  slug: string;
  canonical?: string;
}): string {
  const canonical = article.canonical?.trim();
  if (canonical) return cleanShareUrl(canonical);
  return absoluteUrl(`/article/${encodeURIComponent(article.slug)}`);
}

/** Prefer subtitle for share copy; fall back to title. */
export function articleShareText(article: {
  title: string;
  subtitle?: string;
}): string {
  const subtitle = article.subtitle?.trim();
  if (subtitle) return subtitle;
  return article.title.trim();
}

/** Drop query, hash, and trailing slash so share URLs stay short. */
export function cleanShareUrl(url: string): string {
  try {
    const parsed = new URL(url);
    parsed.search = '';
    parsed.hash = '';
    let href = parsed.toString();
    if (href.endsWith('/') && parsed.pathname !== '/') {
      href = href.slice(0, -1);
    }
    return href;
  } catch {
    const path = url.split('#')[0] || url;
    return path.split('?')[0] || url;
  }
}

export function facebookShareUrl(payload: SharePayload): string {
  return cleanShareUrl(payload.url);
}

export function facebookShareHref(payload: SharePayload): string {
  const u = facebookShareUrl(payload);
  return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(u)}`;
}

export function nativeShareUrl(url: string): string {
  return cleanShareUrl(url);
}

export function isShareAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    (error as { name: string }).name === 'AbortError'
  );
}

export type WebShareData = {
  title?: string;
  text?: string;
  url: string;
};

/** Prefer payloads the current browser will actually present in the share sheet. */
export function pickWebShareData(
  title: string,
  url: string,
  canShare?: (data: WebShareData) => boolean,
): WebShareData {
  const candidates: WebShareData[] = [
    { title, text: title, url },
    { text: title, url },
    { url },
  ];
  if (typeof canShare !== 'function') return candidates[0];
  for (const candidate of candidates) {
    try {
      if (canShare(candidate)) return candidate;
    } catch {
      // Some WebViews throw from canShare instead of returning false.
    }
  }
  return candidates[0];
}

export function whatsappShareHref(payload: SharePayload): string {
  const u = cleanShareUrl(payload.url);
  const message = payload.text ? `${payload.text}\n\n${u}` : u;
  return `https://wa.me/?text=${encodeURIComponent(message)}`;
}

export function telegramShareHref(payload: SharePayload): string {
  const u = cleanShareUrl(payload.url);
  const params = new URLSearchParams({ url: u });
  if (payload.text) params.set('text', payload.text);
  return `https://t.me/share/url?${params.toString()}`;
}

export function twitterShareHref(payload: SharePayload): string {
  const text = payload.text
    ? `${TWITTER_HANDLE} ${payload.text}`
    : TWITTER_HANDLE;
  const params = new URLSearchParams({
    text,
    url: cleanShareUrl(payload.url),
  });
  return `https://x.com/intent/tweet?${params.toString()}`;
}

export function emailShareHref(payload: SharePayload): string {
  const u = cleanShareUrl(payload.url);
  const subject = payload.text || u;
  const body = payload.text ? `${payload.text}\n\n${u}` : u;
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}
